/**
 * Copyright 2025 Lumavec Group LLC
 *
 * Create PR/MR dialog component
 */

import React from 'react';
import { GitPullRequest, Loader } from 'lucide-react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../../ui/alert-dialog';
import type { SourceControlProvider } from '../types';

interface CreatePRDialogProps {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  prTitle: string;
  onPrTitleChange: (title: string) => void;
  prBody: string;
  onPrBodyChange: (body: string) => void;
  prBaseBranch: string;
  onPrBaseBranchChange: (branch: string) => void;
  onCreatePR: () => void;
  creatingPR: boolean;
  sourceControlProvider: SourceControlProvider;
}

export function CreatePRDialog({
  isOpen,
  onOpenChange,
  prTitle,
  onPrTitleChange,
  prBody,
  onPrBodyChange,
  prBaseBranch,
  onPrBaseBranchChange,
  onCreatePR,
  creatingPR,
  sourceControlProvider
}: CreatePRDialogProps) {
  const prLabel = sourceControlProvider === 'github' ? 'Pull Request' : 'Merge Request';
  const prShortLabel = sourceControlProvider === 'github' ? 'PR' : 'MR';
  
  return (
    <AlertDialog open={isOpen} onOpenChange={onOpenChange}>
      <AlertDialogContent>
        <AlertDialogHeader>
          <AlertDialogTitle>Create {prLabel}</AlertDialogTitle>
          <AlertDialogDescription>
            Create a {prLabel.toLowerCase()} for this worktree
          </AlertDialogDescription>
        </AlertDialogHeader>
        
        <div className="space-y-4 my-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Title *
            </label>
            <input
              type="text"
              value={prTitle}
              onChange={(e) => onPrTitleChange(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md"
              placeholder={`Enter ${prShortLabel} title...`}
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Description
            </label>
            <textarea
              value={prBody}
              onChange={(e) => onPrBodyChange(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md"
              rows={4}
              placeholder={`Enter ${prShortLabel} description...`}
            />
          </div>
          
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Base Branch
            </label>
            <input
              type="text"
              value={prBaseBranch}
              onChange={(e) => onPrBaseBranchChange(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md"
            />
          </div>
        </div>
        
        <AlertDialogFooter>
          <AlertDialogCancel disabled={creatingPR}>Cancel</AlertDialogCancel>
          <AlertDialogAction
            onClick={(e) => {
              e.preventDefault();
              onCreatePR();
            }}
            disabled={!prTitle.trim() || creatingPR}
          >
            {creatingPR ? (
              <>
                <Loader size={14} className="animate-spin mr-2" />
                Creating...
              </>
            ) : (
              <>
                <GitPullRequest size={14} className="mr-2" />
                Create {prShortLabel}
              </>
            )}
          </AlertDialogAction>
        </AlertDialogFooter>
      </AlertDialogContent>
    </AlertDialog>
  );
}

